/**
 * UserLogins Section Component
 *
 * Displays successful/failed login counts plus the most recent login attempts.
 */

import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import { formatDateTime } from '../../../../../utils/inspectorFormatters'
import { UserLogins as UserLoginsType, RecentLogin } from '../../../../../services/reportsApi'
import Metric from '../Metric'

interface UserLoginsProps {
  data: UserLoginsType
}

const REASON_LABELS: Record<string, string> = {
  unauthorized_domain: 'Domain blocked',
  no_invitation: 'No invitation',
  invitation_email_mismatch: 'Email mismatch',
}

const formatReason = (reason?: string | null): string | null => {
  if (!reason) return null
  return REASON_LABELS[reason] || reason
}

const RecentRow: React.FC<{ row: RecentLogin }> = ({ row }) => {
  const reasonLabel = formatReason(row.reason)
  const tooltipTitle = row.success
    ? 'Login successful'
    : reasonLabel || 'Login failed'

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Tooltip title={tooltipTitle} placement="top" arrow>
        {row.success ? (
          <CheckCircleIcon fontSize="small" color="success" />
        ) : (
          <CancelIcon fontSize="small" color="error" />
        )}
      </Tooltip>
      <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
        {row.email}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {formatDateTime(row.timestamp)}
      </Typography>
    </Box>
  )
}

const UserLogins: React.FC<UserLoginsProps> = ({ data }) => {
  return (
    <Card id="user-logins" sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6" component="h2">
            User Logins
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Metric label="Successful" value={data.successful} />
          </Grid>
          <Grid item xs={6}>
            <Metric
              label="Failed"
              value={data.failed}
              color={data.failed > 0 ? 'error.main' : 'text.primary'}
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Recent
        </Typography>
        {data.recent.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No login activity
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {data.recent.map((row) => (
              <RecentRow key={`${row.timestamp}-${row.email}`} row={row} />
            ))}
          </Stack>
        )}

        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Button
            component={RouterLink}
            to="/admin/system/audit-logs?event_category=AUTHENTICATION"
            size="small"
          >
            View Audit Logs
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
}

export default UserLogins
